import AcceptedDeliveryTimeSelector from '@/components/OrdersModule/OrderDetail/Footer/AcceptedDeliveryTimeSelector/AcceptedDeliveryTimeSelector'
import AcceptedOrderDetailFooter from '@/components/OrdersModule/OrderDetail/Footer/AcceptedOrderDetailFooter/AcceptedOrderDetailFooter'
import OrderDetailFooterActionButton from '@/components/OrdersModule/OrderDetail/Footer/OrderDetailFooterActionButton'
import TerminalAPI from '@/features/api/terminal/terminal'
import OrderHistoryCollapsible from '@/features/orders/components/OrderDetail/Footer/OrderHistoryCollapsible'
import ContextMenu, {
    ContextMenuProps,
} from '@/features/orders/components/OrderDetail/parts/Footer/ContextMenu'
import FooterLayout from '@/features/orders/components/OrderDetail/parts/Footer/FooterLayout'
import { Phase, State } from '@/features/orders/constants/app'
import { useAppSelector } from '@/hooks/redux'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { isDeliveryOrder } from '@/utils/isDeliveryOrder'
import { FC, ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface OrderDetailFooterProps {
    orderUuid: string
}
interface FooterParts {
    mainContent?: ReactNode
    contextMenu?: ReactElement<ContextMenuProps>
}

const OrderDetailFooter: FC<OrderDetailFooterProps> = ({ orderUuid }) => {
    const order = useAppSelector(ordersSelectors.orderByUuid(orderUuid))

    const { t } = useTranslation()

    if (!order) {
        return null
    }

    if (order.state === State.Accepted && order.phase === Phase.New) {
        return <AcceptedOrderDetailFooter orderUuid={orderUuid} />
    }

    const renderFooterParts = (): FooterParts => {
        if (order.state === State.AwaitingAcceptance) {
            return {
                mainContent: <AcceptedDeliveryTimeSelector orderUuid={orderUuid} />,
            }
        }

        if (order.state === State.Production) {
            const handlePress = async () => {
                await TerminalAPI.finishOrderProduction(orderUuid)
            }

            return {
                mainContent: (
                    <OrderDetailFooterActionButton onPress={handlePress}>
                        {t('order.detail.toReady')}
                    </OrderDetailFooterActionButton>
                ),
                contextMenu: <ContextMenu orderUuid={orderUuid} />,
            }
        }

        if (order.state === State.Ready && isDeliveryOrder(order)) {
            const handlePress = async () => {
                await TerminalAPI.startOrderDelivery(orderUuid)
            }

            return {
                mainContent: (
                    <OrderDetailFooterActionButton onPress={handlePress}>
                        {t('order.detail.toDelivering')}
                    </OrderDetailFooterActionButton>
                ),
                contextMenu: <ContextMenu orderUuid={orderUuid} />,
            }
        }

        if (
            order.state === State.Delivering ||
            (order.state === State.Ready && !isDeliveryOrder(order))
        ) {
            const handlePress = async () => {
                await TerminalAPI.finishOrderDelivery(orderUuid)
            }

            return {
                mainContent: (
                    <OrderDetailFooterActionButton onPress={handlePress}>
                        {t('order.detail.toDelivered')}
                    </OrderDetailFooterActionButton>
                ),
                contextMenu: <ContextMenu orderUuid={orderUuid} />,
            }
        }

        return {
            contextMenu: <ContextMenu orderUuid={orderUuid} />,
        }
    }

    const { mainContent, contextMenu } = renderFooterParts()
    const hasActivityLogs = !!order.activityLogs?.length

    if (!mainContent && !contextMenu && !hasActivityLogs) return null

    return (
        <FooterLayout
            TopContent={
                hasActivityLogs ? (
                    <OrderHistoryCollapsible activityLogs={order.activityLogs} />
                ) : undefined
            }
            ContextMenu={contextMenu}
        >
            {mainContent}
        </FooterLayout>
    )
}

export default OrderDetailFooter
